<script setup lang="ts">

/*
	Walls.vue

	This component implements drawing and editing walls on a 2d canvas.

	FEATURES:
	- Draw new walls by clicking + drag (only when editMode is 'walls')
	- Select, move and delete existing walls
	- Drag wall endpoints to resize them
	- Snap walls to the grid
	- Multiselect walls
	- Visual feedback (wall preview while dragging, highlight selected wall)
*/

import { computed, ref, watch } from 'vue'
import { DEFAULT_GRID_SIZE } from '~/lib/seatmapeditor/constants'
import type { Point, Wall } from '~/lib/seatmapeditor/types'

const props = withDefaults(defineProps<{
	walls: Wall[]
	selectedWallIds: string[]
	pendingWallStart: Point | null
	editMode: 'seats' | 'walls'
	snapToGrid: boolean
	gridSize?: number
	wallColor?: string
	wallSelectedColor?: string
	wallStrokeWidth?: number
	pointerDownPoint: Point | null
	pointerMovePoint: Point | null
	pointerUpPoint: Point | null
}>(), {
	gridSize: DEFAULT_GRID_SIZE
})

const wallBaseColor = computed(() => props.wallColor ?? '#0f172a')
const wallActiveColor = computed(() => props.wallSelectedColor ?? '#0f172a')
const wallBaseStrokeWidth = computed(() => props.wallStrokeWidth ?? 4)

const emit = defineEmits<{
	(e: 'walls-change', payload: Wall[]): void
	(e: 'selection-change', payload: string[]): void
	(e: 'pending-start-change', payload: { x: number; y: number } | null): void
	(e: 'draw-done', payload: string): void
}>()

const selectedWallIdSet = computed(() => new Set(props.selectedWallIds))
const isDrawing = ref(false)
const draftWall = ref<Wall | null>(null)
const WALL_DRAG_THRESHOLD = 2
const wallLineDragState = ref<{ wallId: string; moved: boolean } | null>(null)

const snapWallCoordinate = (value: number) => {
	if (!props.snapToGrid) {
		return value
	}
	return Math.round(value / props.gridSize) * props.gridSize
}

const normalizeWallPoint = (x: number, y: number) => {
	return {
		x: snapWallCoordinate(x),
		y: snapWallCoordinate(y)
	}
}

const getWallById = (wallId: string) => {
	return props.walls.find((wall) => wall.id === wallId)
}

const replaceWall = (wallId: string, update: (wall: Wall) => Wall) => {
	emit('walls-change', props.walls.map((wall) => (wall.id === wallId ? update({ ...wall }) : wall)))
}

const setSelection = (ids: string[]) => {
	emit('selection-change', ids)
}

const setPendingWallStart = (point: Point | null) => {
	emit('pending-start-change', point)
}

const getWallLineConfig = (wall: Wall) => {
	const selected = selectedWallIdSet.value.has(wall.id)
	return {
		points: [wall.x1, wall.y1, wall.x2, wall.y2],
		stroke: selected ? wallActiveColor.value : (wall.stroke ?? wallBaseColor.value),
		strokeWidth: wall.strokeWidth ?? wallBaseStrokeWidth.value,
		lineCap: 'round',
		lineJoin: 'round',
		hitStrokeWidth: 16,
		perfectDrawEnabled: false,
		draggable: props.editMode === 'walls',
		opacity: selected ? 1 : 0.88,
		shadowColor: selected ? 'rgba(37, 99, 235, 0.4)' : 'rgba(15, 23, 42, 0.12)',
		shadowBlur: selected ? 9 : 4,
		shadowOffsetX: 0,
		shadowOffsetY: 2
	}
}

const getWallEndpointConfig = (wall: Wall, endpoint: 'start' | 'end') => {
	const selected = selectedWallIdSet.value.has(wall.id)
	const x = endpoint === 'start' ? wall.x1 : wall.x2
	const y = endpoint === 'start' ? wall.y1 : wall.y2

	return {
		x,
		y,
		radius: 6,
		fill: selected ? '#2563eb' : '#94a3b8',
		stroke: selected ? wallActiveColor.value : wallBaseColor.value,
		strokeWidth: 2,
		perfectDrawEnabled: false,
		draggable: props.editMode === 'walls',
		visible: props.editMode === 'walls'
	}
}

const stopKonvaEvent = (event: any) => {
	event?.evt?.preventDefault?.()
	if (event?.evt) {
		event.evt.cancelBubble = true
	}
	event?.cancelBubble && (event.cancelBubble = true)
}

const onWallLineClick = (wallId: string, event: any) => {
	stopKonvaEvent(event)
	if (props.editMode !== 'walls') {
		return
	}

	const nativeEvt = event?.evt
	const multiSelect = !!(nativeEvt?.ctrlKey || nativeEvt?.metaKey || nativeEvt?.shiftKey)
	if (!multiSelect) {
		setSelection([wallId])
		return
	}

	if (selectedWallIdSet.value.has(wallId)) {
		setSelection(props.selectedWallIds.filter((id) => id !== wallId))
		return
	}

	setSelection([...props.selectedWallIds, wallId])
}

const onWallLineDragStart = (wallId: string, event: any) => {
	stopKonvaEvent(event)
	if (props.editMode !== 'walls') {
		return
	}

	wallLineDragState.value = {
		wallId,
		moved: false
	}

	if (!selectedWallIdSet.value.has(wallId)) {
		setSelection([wallId])
	}
}

const onWallLineDragMove = (wallId: string, node: any) => {
	if (props.editMode !== 'walls') {
		return
	}

	const state = wallLineDragState.value
	if (!state || state.wallId !== wallId) {
		return
	}

	const dx = node.x() ?? 0
	const dy = node.y() ?? 0
	if (Math.hypot(dx, dy) >= WALL_DRAG_THRESHOLD) {
		state.moved = true
	}
}

const onWallLineDragEnd = (wallId: string, node: any) => {
	if (props.editMode !== 'walls') {
		return
	}

	const state = wallLineDragState.value
	wallLineDragState.value = null

	if (!state || state.wallId !== wallId || !state.moved) {
		node.position({ x: 0, y: 0 })
		return
	}

	const wall = getWallById(wallId)
	if (!wall) {
		node.position({ x: 0, y: 0 })
		return
	}

	const dx = node.x() ?? 0
	const dy = node.y() ?? 0
	let nextX1 = wall.x1 + dx
	let nextY1 = wall.y1 + dy
	let nextX2 = wall.x2 + dx
	let nextY2 = wall.y2 + dy

	if (props.snapToGrid) {
		const start = normalizeWallPoint(nextX1, nextY1)
		const end = normalizeWallPoint(nextX2, nextY2)
		nextX1 = start.x
		nextY1 = start.y
		nextX2 = end.x
		nextY2 = end.y
	}

	replaceWall(wallId, (target) => ({
		...target,
		x1: nextX1,
		y1: nextY1,
		x2: nextX2,
		y2: nextY2
	}))

	emit('draw-done', wallId)

	node.position({ x: 0, y: 0 })
}

const onWallEndpointDragMove = (wallId: string, endpoint: 'start' | 'end', node: any) => {
	if (props.editMode !== 'walls') {
		return
	}

	const x = node.x()
	const y = node.y()
	replaceWall(wallId, (wall) => {
		if (endpoint === 'start') {
			wall.x1 = x
			wall.y1 = y
		} else {
			wall.x2 = x
			wall.y2 = y
		}
		return wall
	})
}

const onWallEndpointDragEnd = (wallId: string, endpoint: 'start' | 'end', node: any) => {
	onWallEndpointDragMove(wallId, endpoint, node)
	if (!props.snapToGrid) {
		emit('draw-done', wallId)
		return
	}

	const wall = getWallById(wallId)
	if (!wall) {
		return
	}

	if (endpoint === 'start') {
		const start = normalizeWallPoint(wall.x1, wall.y1)
		replaceWall(wallId, (target) => ({ ...target, x1: start.x, y1: start.y }))
		node.position({ x: start.x, y: start.y })
	} else {
		const end = normalizeWallPoint(wall.x2, wall.y2)
		replaceWall(wallId, (target) => ({ ...target, x2: end.x, y2: end.y }))
		node.position({ x: end.x, y: end.y })
	}

	emit('draw-done', wallId)
}

watch(
	() => props.pointerDownPoint,
	(point) => {
		if (!point || props.editMode !== 'walls') {
			return
		}

		const snapped = normalizeWallPoint(point.x, point.y)
		isDrawing.value = true
		setPendingWallStart(snapped)
		setSelection([])
		draftWall.value = {
			id: 'wall-draft',
			x1: snapped.x,
			y1: snapped.y,
			x2: snapped.x,
			y2: snapped.y,
			stroke: wallBaseColor.value,
			strokeWidth: wallBaseStrokeWidth.value
		}
	}
)


watch(
	() => props.pointerMovePoint,
	(point) => {
		if (!point || props.editMode !== 'walls' || !isDrawing.value || !props.pendingWallStart) {
			return
		}

		const snapped = normalizeWallPoint(point.x, point.y)
		draftWall.value = {
			id: 'wall-draft',
			x1: props.pendingWallStart.x,
			y1: props.pendingWallStart.y,
			x2: snapped.x,
			y2: snapped.y,
			stroke: wallBaseColor.value,
			strokeWidth: wallBaseStrokeWidth.value
		}
	}
)


watch(
	() => props.pointerUpPoint,
	(point) => {
		if (!point || props.editMode !== 'walls' || !isDrawing.value || !props.pendingWallStart) {
			return
		}

		const start = props.pendingWallStart
		const end = normalizeWallPoint(point.x, point.y)
		isDrawing.value = false
		draftWall.value = null

		if (start.x === end.x && start.y === end.y) {
			setPendingWallStart(start)
			return
		}

		setPendingWallStart(null)

		const wallId = `wall-${Date.now()}-${Math.round(Math.random() * 100000)}`
		emit('walls-change', [
			...props.walls,
			{
				id: wallId,
				x1: start.x,
				y1: start.y,
				x2: end.x,
				y2: end.y,
				stroke: wallBaseColor.value,
				strokeWidth: wallBaseStrokeWidth.value
			}
		])
		setSelection([wallId])
		emit('draw-done', wallId)
	}
)
</script>

<template>
	<v-group>
		<template v-for="wall in walls" :key="`wall-${wall.id}`">
			<v-line
				:config="getWallLineConfig(wall)"
				@click="onWallLineClick(wall.id, $event)"
				@dragstart="onWallLineDragStart(wall.id, $event)"
				@dragmove="onWallLineDragMove(wall.id, $event.target)"
				@dragend="onWallLineDragEnd(wall.id, $event.target)"
			/>
			<v-circle
				:config="getWallEndpointConfig(wall, 'start')"
				@dragmove="onWallEndpointDragMove(wall.id, 'start', $event.target)"
				@dragend="onWallEndpointDragEnd(wall.id, 'start', $event.target)"
			/>
			<v-circle
				:config="getWallEndpointConfig(wall, 'end')"
				@dragmove="onWallEndpointDragMove(wall.id, 'end', $event.target)"
				@dragend="onWallEndpointDragEnd(wall.id, 'end', $event.target)"
			/>
		</template>

		<v-circle
			v-if="pendingWallStart"
			:config="{
				x: pendingWallStart.x,
				y: pendingWallStart.y,
				radius: 6,
				fill: '#0ea5e9',
				stroke: '#ffffff',
				strokeWidth: 2,
				listening: false
			}"
		/>

		<v-line
			v-if="draftWall"
			:config="{
				points: [draftWall.x1, draftWall.y1, draftWall.x2, draftWall.y2],
				stroke: draftWall.stroke,
				strokeWidth: draftWall.strokeWidth,
				lineCap: 'round',
				lineJoin: 'round',
				dash: [8, 6],
				listening: false,
				opacity: 0.8
			}"
		/>
	</v-group>
</template>